import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserFromMe } from "@/features/auth/authSlice";
import { queryClient } from "@/config/queryClient";
import { apiService } from "@/services/api";
import { useUserStore } from "@/store/useUserStore";
import { getPwaShellState, isPlusOrHigher, normalizeTier } from "@/utils/pwa";

const PWA_SYNC_EVENT = "finu:pwa-install-synced";

const usePwaInstallSync = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { isPwaShell } = getPwaShellState();
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const currentTier = normalizeTier(user?.tier || user?.plan_tier || user?.subscription);
    if (isPlusOrHigher(currentTier)) return;

    const syncPwaInstall = async () => {
      if (syncInFlightRef.current) return;
      syncInFlightRef.current = true;
      try {
        const updatedUser = await apiService.confirmPwaInstall();
        if (!updatedUser) return;

        dispatch(setUserFromMe(updatedUser));
        useUserStore.getState().updateUser(updatedUser);
        queryClient.setQueryData(["users-me"], updatedUser);

        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(PWA_SYNC_EVENT, {
              detail: {
                userId: user.id,
                updatedUser,
                isFirstSync: true,
              },
            }),
          );
        }
      } catch (error) {
        console.warn("Failed to sync PWA install entitlement:", error);
      } finally {
        syncInFlightRef.current = false;
      }
    };

    const handleAppInstalled = () => {
      syncPwaInstall();
    };

    if (isPwaShell) {
      syncPwaInstall();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("appinstalled", handleAppInstalled);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("appinstalled", handleAppInstalled);
      }
    };
  }, [dispatch, isAuthenticated, isPwaShell, user?.id, user?.tier, user?.plan_tier, user?.subscription]);
};

export default usePwaInstallSync;
